import React, { useState } from "react";
import { ReportCard } from "../../types";
import RoseChart from "./RoseChart";
import ReadoutBar from "./ReadoutBar";
import {
  ActiveSlice,
  bandFor,
  groupByKind,
  sharedBands,
  splitSummary,
  weightedGrade,
} from "./reportCardModel";

interface Props {
  card: ReportCard;
  onSelectCriterion?: (criterionId: string) => void;
}

/**
 * The masthead: what the run was and what it came to, on the left; the two
 * charts and their shared readout on the right.
 */
const ReportCardHeader: React.FC<Props> = ({ card, onSelectCriterion }) => {
  // Hover and focus state lives here rather than in the DOM, so one slice being
  // active is a single fact both charts and the readout read from.
  const [active, setActive] = useState<ActiveSlice | null>(null);

  const { conversation, solution } = groupByKind(card.sections);
  const { headline, body } = splitSummary(card.summary);

  const convBands = sharedBands(conversation);
  const solBands = sharedBands(solution);
  // Only when every criterion on the card agrees can one legend, or one list of
  // threshold numbers, describe both charts at once. Otherwise each slice
  // carries its own thresholds and the readout says so instead of quoting one
  // criterion's numbers at all of them.
  const cardBands = sharedBands(card.sections);

  const convGrade = weightedGrade(conversation);
  const solGrade = weightedGrade(solution);

  const facts: { term: string; value: string }[] = [
    { term: "Rubric", value: card.rubricName },
    { term: "Framework", value: card.frameworkName },
    {
      term: "Stakeholders",
      value: `${card.personasContacted} of ${card.personaCount} interviewed`,
    },
    {
      term: "Graded",
      value: new Date(card.createdAt).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    },
  ];

  return (
    <header>
      <div className="masthead">
        <div>
          <p className="eyebrow">
            Debrief &nbsp;·&nbsp; Session {card.sessionId.slice(0, 8).toUpperCase()}{" "}
            &nbsp;·&nbsp; <b>{card.scenarioTitle}</b>
          </p>

          {headline && <h1>{headline}</h1>}
          {body && <p className="standfirst">{body}</p>}
        </div>

        <div className="glance">
          <div className="roses">
            {conversation.length > 0 && (
              <RoseChart
                label="Conversation"
                kind="conversation"
                sections={conversation}
                colorOffset={0}
                grade={convGrade}
                bandName={
                  convBands && convGrade !== null
                    ? (bandFor(convBands, convGrade)?.name ?? null)
                    : null
                }
                activeCriterionId={active?.criterionId ?? null}
                onActivate={setActive}
                onSelect={onSelectCriterion}
              />
            )}
            {solution.length > 0 && (
              <RoseChart
                label="Solution"
                kind="solution"
                sections={solution}
                colorOffset={conversation.length}
                grade={solGrade}
                bandName={
                  solBands && solGrade !== null
                    ? (bandFor(solBands, solGrade)?.name ?? null)
                    : null
                }
                activeCriterionId={active?.criterionId ?? null}
                onActivate={setActive}
                onSelect={onSelectCriterion}
              />
            )}
          </div>

          <ReadoutBar active={active} bands={cardBands} />
        </div>
      </div>

      <dl className="facts">
        {facts.map(({ term, value }) => (
          <div key={term}>
            <dt>{term}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      {/* Shown only on the narrow layout, where the aligned axis is dropped. */}
      {cardBands && (
        <div className="legend">
          <span>Bands</span>
          {cardBands.map((level) => (
            <span key={level.name}>
              {level.name} {level.minGrade}&ndash;{level.maxGrade}
            </span>
          ))}
        </div>
      )}
    </header>
  );
};

export default ReportCardHeader;
